import _ from 'lodash';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { AgGridReact } from 'ag-grid-react';
import { IsFullWidthRowParams } from 'ag-grid-community';
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  FormProps,
  Row,
  Select,
  SelectProps,
  Tag,
  Input
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { SearchOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';

import QUERY_KEYS from 'services/api/queryKeys';
import { handleRangeDateChange, toUtcTime } from 'utils';
import { getCashierGuestsColumns } from 'constants/ag-grid-table';
import 'styles/cashier.scss';
import { useGetGuestOrders, useGetGuestTransactions } from 'hooks/useGetGuestTransactions';
import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import { CashierReservation } from './components/CashierReservation';
import { CashierTransaction } from './components/CashierTransaction';
import { BookingStatus, ParamsGetTransactionsType } from 'services/api/type/booking.type';
import useCashierStore from 'stores/useCashier';

const initStartDate = dayjs().set('hour', 0).set('minute', 0).set('second', 0);
const initEndDate = dayjs().set('hour', 23).set('minute', 59).set('second', 59);

type TagRender = SelectProps['tagRender'];

function Cashier() {
  const { t } = useTranslation();

  // Shared function to get field labels
  const getFieldLabel = useCallback(
    (fieldType: string) => {
      switch (fieldType) {
        case 'guest_info':
          return t('cashierPage.guestInfo');
        case 'group_info':
          return t('cashierPage.group');
        case 'travel_agency_info':
          return t('groupPage.createGroup.taCompany');
        case 'room_no':
          return t('cashierPage.roomNo');
        case 'sale_order_name':
          return t('Sale Order');
        default:
          return fieldType;
      }
    },
    [t]
  );

  const tagRender: TagRender = props => {
    // eslint-disable-next-line react/prop-types
    const { value, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    // Get the field type and actual value from the condition
    // eslint-disable-next-line react/prop-types
    const [fieldType, actualValue] = value.split('|');

    const displayLabel = getFieldLabel(fieldType);

    return (
      <Tag
        color="#9c4f3b"
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginInlineEnd: 4, padding: '2px 7px' }}
      >
        <b>{displayLabel}</b>: {actualValue}
      </Tag>
    );
  };

  const [form] = Form.useForm();

  const { selectedReservationId, selectedGroupId, setSelectedReservationId, setSelectedGroupId } =
    useCashierStore();

  const [searchCombined, setSearchCombined] = useState('');
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [filterOptions, setFilterOptions] = useState<ParamsGetTransactionsType>({
    main_guest_only: true
  });
  const [enableArrivalTime, setEnableArrivalTime] = useState<boolean>(false);
  const [enableDepartureTime, setEnableDepartureTime] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1);

  const { data: guests, hasMore } = useGetGuestTransactions(filterOptions, page);
  const [selectedRow, setSelectedRow] = useState<any>(undefined);
  const { data: bookingLine } = useGetBookingDetail(selectedRow?.booking_line_id);
  const { data: orders } = useGetGuestOrders(selectedRow?.id);

  const queryClient = useQueryClient();

  const gridRef = useRef<any>(null);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GET_CASH_FLOW, page]
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GET_CASH_FLOW_ALL]
    });
  }, [filterOptions, page, queryClient]);

  useEffect(() => {
    if (selectedReservationId) {
      const guestInfoCondition = `guest_info|${selectedReservationId}`;
      setFilterOptions((prevOptions: any) => ({
        ...prevOptions,
        guest_info: selectedReservationId
      }));
      form.setFieldsValue({
        guest_info: selectedReservationId
      });
      setSelectedConditions(prev => {
        if (!prev.some(condition => condition.startsWith('guest_info|'))) {
          return [...prev, guestInfoCondition];
        }
        return prev.map(condition =>
          condition.startsWith('guest_info|') ? guestInfoCondition : condition
        );
      });
      setSelectedReservationId('');
    }
  }, [selectedReservationId, form, setSelectedReservationId]);

  useEffect(() => {
    if (selectedGroupId) {
      const groupInfoCondition = `group_info|${selectedGroupId}`;
      setFilterOptions((prevOptions: any) => ({
        ...prevOptions,
        group_info: selectedGroupId
      }));
      form.setFieldsValue({
        group_info: selectedGroupId
      });
      setSelectedConditions(prev => {
        if (!prev.some(condition => condition.startsWith('group_info|'))) {
          return [...prev, groupInfoCondition];
        }
        return prev.map(condition =>
          condition.startsWith('group_info|') ? groupInfoCondition : condition
        );
      });
      setSelectedGroupId('');
    }
  }, [selectedGroupId, form, setSelectedGroupId]);

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const {
      check_in_range,
      check_out_range,
      is_co_today,
      is_reserved,
      is_in_house,
      is_checked_out,
      ...restFilterOptions
    } = truthyValues;

    const configMap = [
      {
        condition: check_in_range && enableArrivalTime,
        actions: () => {
          const startDate: Dayjs = check_in_range[0];
          const endDate: Dayjs = check_in_range[1];
          if (startDate)
            _.set(
              restFilterOptions,
              'check_in_from',
              toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD')
            );
          if (endDate)
            _.set(restFilterOptions, 'check_in_to', toUtcTime(endDate.add(1, 'day'), 'YYYY-MM-DD'));
        }
      },
      {
        condition: check_out_range && enableDepartureTime,
        actions: () => {
          const startDate: Dayjs = check_out_range[0];
          const endDate: Dayjs = check_out_range[1];
          if (startDate)
            _.set(
              restFilterOptions,
              'check_out_from',
              toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD')
            );
          if (endDate)
            _.set(
              restFilterOptions,
              'check_out_to',
              toUtcTime(endDate.add(1, 'day'), 'YYYY-MM-DD')
            );
        }
      },
      {
        condition: is_co_today || is_reserved || is_in_house || is_checked_out,
        actions: () => {
          const statusBarList = [];
          if (is_co_today) {
            const today = dayjs().format('YYYY-MM-DD');
            _.set(restFilterOptions, 'check_out_from', today);
            _.set(restFilterOptions, 'check_out_to', today);
          }
          if (is_reserved) statusBarList.push(BookingStatus.INITIAL);
          if (is_co_today || is_in_house) statusBarList.push(BookingStatus.CHECK_IN);
          if (is_checked_out) statusBarList.push(BookingStatus.CHECK_OUT);
          if (statusBarList.length > 0) {
            _.set(restFilterOptions, 'status_bar', statusBarList.join(','));
          }
        }
      }
    ];

    configMap.forEach(({ condition, actions }) => {
      if (condition) actions();
    });

    if (page !== 1) setPage(1);

    setFilterOptions(restFilterOptions);
  };

  const isFullWidthRow = (params: IsFullWidthRowParams) => {
    return params.rowNode.data?.is_party;
  };

  const cashierGuestsColumns = useMemo(() => getCashierGuestsColumns(t), [t]);

  const handleCheckboxArrivalClick = (e: any) => {
    setEnableArrivalTime(e.target.checked);
  };

  const handleCheckboxDepartureClick = (e: any) => {
    setEnableDepartureTime(e.target.checked);
  };

  const handleClickParty = (value: string) => {
    setSelectedReservationId(value);
  };

  const handleClickGroupId = (value: string) => {
    setSelectedGroupId(value);
  };

  const handleLoadMore = () => {
    setPage(prevPage => prevPage + 1);
  };

  const debouncedHandleLoadMore = _.debounce(handleLoadMore, 400);

  const onGridScroll = useCallback(
    (params: any) => {
      const { direction } = params;
      if (direction !== 'vertical') return;

      const gridElement = document.querySelector('.ag-body-viewport');
      if (!gridElement) {
        return;
      }

      const scrollHeight = gridElement.scrollHeight;
      const clientHeight = gridElement.clientHeight;
      const scrollPosition = params.top;

      const isBottom = scrollPosition + clientHeight >= scrollHeight;

      if (isBottom && hasMore) {
        debouncedHandleLoadMore();
      }
    },
    // eslint-disable-next-line
    [hasMore]
  );

  const options: SelectProps['options'] = useMemo(() => {
    const allOptions = [
      {
        label: getFieldLabel('guest_info'),
        fieldValue: 'guest_info',
        value: `guest_info|${searchCombined}`
      },
      {
        label: getFieldLabel('group_info'),
        fieldValue: 'group_info',
        value: `group_info|${searchCombined}`
      },
      {
        label: getFieldLabel('travel_agency_info'),
        fieldValue: 'travel_agency_info',
        value: `travel_agency_info|${searchCombined}`
      },
      {
        label: getFieldLabel('room_no'),
        fieldValue: 'room_no',
        value: `room_no|${searchCombined}`
      },
      {
        label: getFieldLabel('sale_order_name'),
        fieldValue: 'sale_order_name',
        value: `sale_order_name|${searchCombined}`
      }
    ];

    // If search starts with 'S' or 's', prioritize sale_order_name
    const orderedOptions = searchCombined.toLowerCase().startsWith('s')
      ? allOptions.sort((a, b) => {
          if (a.fieldValue === 'sale_order_name') return -1;
          if (b.fieldValue === 'sale_order_name') return 1;
          return 0;
        })
      : allOptions;

    return orderedOptions.filter(item =>
      selectedConditions.every(condition => !condition.includes(item.fieldValue))
    );
  }, [searchCombined, selectedConditions, getFieldLabel]);

  return (
    <div className="pms-cashier">
      <Row gutter={[8, 16]} className="pms-cashier__row">
        <Col xs={24} xl={12} xxl={12} className="flex flex-col h-full">
          <Card style={{ width: '100%' }}>
            <Form
              form={form}
              name="cashier-filter-form"
              layout={'inline'}
              initialValues={{
                guest_info: undefined,
                group_info: undefined,
                travel_agency_info: undefined,
                room_no: undefined,
                sale_order_name: undefined,
                arrivalTime: [initStartDate, initEndDate],
                main_guest_only: true
              }}
              onFinish={onFinish}
              autoComplete="off"
            >
              <div className="w-full">
                <Row gutter={[8, 8]} className="w-full" style={{ marginBottom: 8 }}>
                  <Col span={24} xxl={12} className="relative" style={{ paddingRight: 12 }}>
                    <SearchOutlined
                      style={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: '16px',
                        zIndex: 99
                      }}
                    />

                    <Select
                      open={!!searchCombined}
                      mode="multiple"
                      tagRender={tagRender}
                      style={{ width: '100%' }}
                      options={options}
                      value={selectedConditions}
                      className="pms-cashier__search-select"
                      optionRender={option => (
                        <Trans
                          i18nKey="cashierPage.searchFor.title"
                          values={{ field: t(option.label as string), value: searchCombined }}
                          components={{
                            bold: <strong />,
                            i: <i className="text-secondary" />
                          }}
                        />
                      )}
                      onSearch={setSearchCombined}
                      filterOption={false}
                      onSelect={e => {
                        setSelectedConditions([...selectedConditions, e]);
                        setSearchCombined('');
                        form.setFieldValue(e.split('|')[0], e.split('|')[1]);
                      }}
                      onDeselect={e => {
                        setSelectedConditions(
                          selectedConditions.filter(condition => condition !== e)
                        );
                        form.setFieldValue(e.split('|')[0], undefined);
                      }}
                      onBlur={() => setSearchCombined('')}
                    />
                  </Col>
                  <Col span={0} xxl={4}></Col>
                </Row>

                <div style={{ display: 'none' }}>
                  <Col span={12}>
                    <Form.Item
                      label={t('cashierPage.guestInfo')}
                      name="guest_info"
                      wrapperCol={{ span: 13 }}
                      labelCol={{ span: 10 }}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label={t('cashierPage.group')}
                      name="group_info"
                      wrapperCol={{ span: 13 }}
                      labelCol={{ span: 10 }}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label={t('groupPage.createGroup.taCompany')}
                      name="travel_agency_info"
                      wrapperCol={{ span: 13 }}
                      labelCol={{ span: 10 }}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label={t('cashierPage.roomNo')}
                      name="room_no"
                      wrapperCol={{ span: 13 }}
                      labelCol={{ span: 10 }}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={t('Sale Order')}
                      name="sale_order_name"
                      wrapperCol={{ span: 13 }}
                      labelCol={{ span: 10 }}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </div>
              </div>

              <Row gutter={[8, 8]} className="w-full" style={{ marginBottom: 8 }}>
                <Col span={24} xxl={12}>
                  {/* Arrival */}
                  <Row gutter={[8, 8]} className="w-full">
                    <Col span={7}>
                      <Form.Item valuePropName="checked">
                        <Checkbox onClick={handleCheckboxArrivalClick}>
                          {t('cashierPage.arrival')}
                        </Checkbox>
                      </Form.Item>
                    </Col>

                    <Col span={17}>
                      <Form.Item name="check_in_range" valuePropName="checked" className="w-full">
                        <DatePicker.RangePicker
                          className="w-full"
                          placeholder={[t('common.time.startDate'), t('common.time.endDate')]}
                          format="DD-MM-YYYY"
                          onChange={value => handleRangeDateChange(form, value, 'check_in')}
                          disabled={!enableArrivalTime}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>

                <Col span={24} xxl={12}>
                  {/* Departure */}
                  <Row gutter={[8, 8]} className="w-full">
                    <Col span={7}>
                      <Form.Item valuePropName="checked">
                        <Checkbox onClick={handleCheckboxDepartureClick}>
                          {t('cashierPage.departure')}
                        </Checkbox>
                      </Form.Item>
                    </Col>

                    <Col span={17}>
                      <Form.Item name="check_out_range" valuePropName="checked" className="w-full">
                        <DatePicker.RangePicker
                          className="w-full"
                          placeholder={[t('common.time.startDate'), t('common.time.endDate')]}
                          format="DD-MM-YYYY"
                          onChange={value => handleRangeDateChange(form, value, 'check_out')}
                          disabled={!enableDepartureTime}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Flex
                align="center"
                justify="space-between"
                wrap
                className="w-full checkbox-wrapper"
                style={{ marginBottom: 12 }}
              >
                <Form.Item name="non_zero_balance" valuePropName="checked">
                  <Checkbox>{t('cashierPage.nonZeroBalance')}</Checkbox>
                </Form.Item>

                <Form.Item name="is_co_today" valuePropName="checked">
                  <Checkbox>{t('cashierPage.coToday')}</Checkbox>
                </Form.Item>

                <Form.Item name="is_reserved" valuePropName="checked">
                  <Checkbox>{t('cashierPage.reserved')}</Checkbox>
                </Form.Item>

                <Form.Item name="is_in_house" valuePropName="checked">
                  <Checkbox>{t('cashierPage.inHouse')}</Checkbox>
                </Form.Item>

                <Form.Item name="is_checked_out" valuePropName="checked">
                  <Checkbox>{t('cashierPage.checkedOut')}</Checkbox>
                </Form.Item>
              </Flex>

              <Flex align="center" justify="space-between" wrap className="w-full checkbox-wrapper">
                <Form.Item name="main_guest_only" valuePropName="checked">
                  <Checkbox>{t('cashierPage.mainGuestOnly')}</Checkbox>
                </Form.Item>
                <Button htmlType="submit" className="ant-btn-secondary btn-submit ml-auto">
                  {t('common.actions.submitButton')}
                </Button>
              </Flex>
            </Form>
          </Card>

          <Card
            style={{ width: '100%', marginTop: 4, flex: 1 }}
            className="flex flex-col pms-cashier-guests"
          >
            <p className="title">{t('cashierPage.listGuest')}</p>
            <div className="pms-cashier-guests__table">
              <div className="pms-cashier-guests__table ag-theme-quartz">
                <AgGridReact
                  ref={gridRef}
                  rowData={guests}
                  columnDefs={cashierGuestsColumns}
                  onBodyScroll={onGridScroll}
                  domLayout="normal"
                  onRowClicked={event => {
                    if (event.data?.is_party) {
                      return;
                    }
                    setSelectedRow(event.data);
                  }}
                  getRowId={params => params.data.id}
                  stopEditingWhenCellsLoseFocus
                  getRowClass={params => {
                    return params.data.id === selectedRow?.id ? 'highlight-row' : '';
                  }}
                  isFullWidthRow={isFullWidthRow}
                  fullWidthCellRenderer={(item: any) => {
                    const data = item.data;
                    const group = data.group_id ? (
                      <>
                        {t('common.customer.group')}:{' '}
                        <>
                          <a
                            href="#"
                            onClick={e => {
                              e.preventDefault();
                              handleClickGroupId(String(data.group_id));
                            }}
                            className="hightlight"
                          >
                            {data.group_id}
                          </a>
                        </>{' '}
                        - {data.group_name} {!item.data.party_id && `(${item.data.booking_count})`}
                        {item.data.party_id && (
                          <>
                            | Party:{' '}
                            <a
                              href="#"
                              onClick={e => {
                                e.preventDefault();
                                handleClickParty(String(item.data.party_id));
                              }}
                              className="hightlight"
                            >
                              {item.data.party_id}
                            </a>{' '}
                            ({item.data.booking_count})
                          </>
                        )}
                      </>
                    ) : (
                      ''
                    );
                    return (
                      <div
                        className="flex items-center h-full fullwidth-row"
                        style={{ paddingLeft: 16 }}
                      >
                        <p className="m-0">
                          {group ? (
                            group
                          ) : item.data.tour_info ? (
                            (() => {
                              const tourParts = item.data.tour_info.split(' -- ');
                              const bookingId = tourParts[0];
                              const tourName = tourParts[1] || '';

                              return (
                                <>
                                  ID:{' '}
                                  <a
                                    href="#"
                                    onClick={e => {
                                      e.preventDefault();
                                      handleClickParty(bookingId);
                                    }}
                                    style={{
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      color: 'blue'
                                    }}
                                  >
                                    {bookingId}
                                  </a>
                                  {tourName && (
                                    <>
                                      {' - '}
                                      Tour:
                                      <span
                                        style={{
                                          color: '#800080',
                                          marginLeft: '4px'
                                        }}
                                      >
                                        {tourName}
                                      </span>
                                    </>
                                  )}
                                </>
                              );
                            })()
                          ) : item.data.party_id === 'FIT' ? (
                            'FIT'
                          ) : (
                            <>
                              Party:{' '}
                              <a
                                href="#"
                                onClick={e => {
                                  e.preventDefault();
                                  handleClickParty(String(item.data.party_id));
                                }}
                                style={{
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  color: 'blue'
                                }}
                              >
                                {item.data.party_id}
                              </a>{' '}
                              ({item.data.booking_count})
                            </>
                          )}
                        </p>
                      </div>
                    );
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={12} xxl={12} className="flex flex-col">
          <CashierReservation bookingLine={bookingLine} orders={orders} />

          <CashierTransaction
            orders={orders}
            guest_id={selectedRow?.id}
            booking_line_id={bookingLine?.booking_line_id}
          />
        </Col>
      </Row>
    </div>
  );
}

export default Cashier;
